/**
 * Restaurant Menu Service
 * 
 * This service provides an abstraction layer for retrieving restaurant menu data.
 * It can be extended to integrate with real APIs (Yelp, DoorDash, Uber Eats, Google Places, etc.)
 * 
 * For now, it uses an in-memory dataset, but the interface is designed to support
 * async API calls in the future.
 */

import type { RestaurantMenuItem } from '../data/restaurantMenus';
import { getRestaurantMenuItems, fuzzyMatchRestaurantName } from '../data/restaurantMenus';
import { searchInternetMenu, isInternetMenuSearchAvailable } from './internetMenuService';

export interface RestaurantMenu {
  restaurantName: string;
  items: RestaurantMenuItem[];
  isExactMatch: boolean; // true if we have the exact restaurant's menu, false if it's a template/pattern match
  source: 'database' | 'template' | 'api'; // Future: support 'api' for real-time menu data
}

export interface RestaurantMenuService {
  /**
   * Get menu for a specific restaurant
   * @param restaurantName - The name of the restaurant
   * @returns Promise resolving to RestaurantMenu or null if no menu found
   */
  getMenuForRestaurant(restaurantName: string): Promise<RestaurantMenu | null>;
}

/**
 * In-memory restaurant menu service implementation
 * 
 * This uses the existing restaurantMenus.ts data.
 * In the future, this can be extended to call real APIs.
 */
class InMemoryRestaurantMenuService implements RestaurantMenuService {
  /**
   * Known exact restaurant matches (restaurants with specific menu items in the database)
   * These are the ONLY restaurants we have menus for. All others return null.
   */
  private readonly knownExactRestaurants = [
    'topside tavern', 'topside',
    'flavors of namasthe india', 'flavors of namasthe', 'namasthe india', 'namasthe',
    'cheesecake factory', 'the cheesecake factory', 'mcdonald\'s', 'mcdonalds',
    'dunkin\' donuts', 'dunkin donuts', 'dunkin', 'wendy\'s', 'wendys',
    'burger king', 'subway', 'kfc', 'taco bell', 'chipotle',
    'olive garden', 'applebee\'s', 'applebees', 'outback', 'outback steakhouse',
    'red lobster', 'panda express',
    // Note: Removed generic matches like 'thanal', 'bombay', 'delhi', 'tandoor', 'curry', etc.
    // These were using template menus. Only include restaurants with specific menus.
  ];

  async getMenuForRestaurant(restaurantName: string): Promise<RestaurantMenu | null> {
    const lowerName = restaurantName.toLowerCase().trim();
    
    // Step 1: Try local database first (fastest, most accurate)
    const menuItems = getRestaurantMenuItems(restaurantName);
    
    if (menuItems && menuItems.length > 0) {
      // Found in local database
      const isExactMatch = this.knownExactRestaurants.some(exact => 
        lowerName === exact || lowerName.includes(exact) || exact.includes(lowerName)
      ) || fuzzyMatchRestaurantName(restaurantName) !== null;
      
      return {
        restaurantName,
        items: menuItems,
        isExactMatch: isExactMatch || false,
        source: 'database',
      };
    }
    
    // Step 2: If not found locally and internet search is available, try internet
    if (isInternetMenuSearchAvailable()) {
      try {
        console.log('[RestaurantMenuService] Searching internet for menu:', restaurantName);
        const internetResult = await searchInternetMenu(restaurantName);
        
        if (internetResult && internetResult.items.length > 0) {
          console.log('[RestaurantMenuService] Internet search successful:', {
            restaurantName: internetResult.restaurantName,
            itemsCount: internetResult.items.length,
            source: internetResult.source,
            confidence: internetResult.confidence
          });
          return {
            restaurantName: internetResult.restaurantName,
            items: internetResult.items,
            isExactMatch: internetResult.confidence === 'high',
            source: 'api', // Mark as API source
          };
        } else {
          console.log('[RestaurantMenuService] Internet search returned no results for:', restaurantName);
        }
      } catch (error) {
        console.error('[RestaurantMenuService] Error searching internet for menu:', error);
        // Fall through to return null
      }
    } else {
      console.log('[RestaurantMenuService] Internet menu search not available (no API keys configured)');
    }
    
    // Step 3: No menu found in database or internet
    return null;
  }
}

// Export singleton instance
export const restaurantMenuService: RestaurantMenuService = new InMemoryRestaurantMenuService();

/**
 * Fuzzy match a dish name against menu items
 * Returns the best matching menu item or null if no good match found
 */
export function fuzzyMatchDish(
  dishName: string,
  menuItems: RestaurantMenuItem[]
): RestaurantMenuItem | null {
  const lowerDishName = dishName.toLowerCase().trim();
  
  // First, try exact match
  const exactMatch = menuItems.find(item => 
    item.name.toLowerCase().trim() === lowerDishName
  );
  if (exactMatch) {
    return exactMatch;
  }
  
  // Try partial match (dish name contains menu item name or vice versa)
  const partialMatch = menuItems.find(item => {
    const lowerItemName = item.name.toLowerCase().trim();
    return lowerDishName.includes(lowerItemName) || lowerItemName.includes(lowerDishName);
  });
  if (partialMatch) {
    return partialMatch;
  }
  
  // Try word-based matching (at least 2 words match)
  const dishWords = lowerDishName.split(/\s+/).filter(w => w.length > 2);
  const wordMatch = menuItems.find(item => {
    const itemWords = item.name.toLowerCase().trim().split(/\s+/).filter(w => w.length > 2);
    const matchingWords = dishWords.filter(dw => 
      itemWords.some(iw => iw.includes(dw) || dw.includes(iw))
    );
    return matchingWords.length >= 2;
  });
  if (wordMatch) {
    return wordMatch;
  }
  
  return null;
}


